import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { MemberRole, MemberStatus } from '../types/models';

export interface Permissions {
  role: MemberRole | undefined;
  status: MemberStatus | undefined;
  isActive: boolean;
  isPending: boolean;
  isRejected: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
  canEdit: boolean;           // Add/edit/delete items, CSV import/export, bulk ops
  canManageMembers: boolean;  // Approve/reject, change roles, invite code
  canCheckInOut: boolean;     // All active users
  canViewInventory: boolean;  // Pending users cannot view
  hasOrganization: boolean;   // User belongs to an org
}

export function usePermissions(): Permissions {
  const user = useSelector((state: RootState) => state.auth.user);

  const role = user?.role;
  const status = user?.memberStatus;

  // Legacy support: if no role field, fall back to adminStatus
  const effectiveRole = role || (user?.adminStatus === 1 ? 'admin' : undefined);

  // Users without an org (guest/Google) are treated as active editors (full access to shared pool)
  const hasOrganization = !!user?.organizationID;
  const isActive = !hasOrganization || status === 'active';
  const isPending = hasOrganization && status === 'pending';
  const isRejected = hasOrganization && status === 'rejected';

  const isAdmin = effectiveRole === 'admin';
  const isEditor = effectiveRole === 'editor';
  const isViewer = effectiveRole === 'viewer';

  return {
    role: effectiveRole as MemberRole | undefined,
    status,
    isActive,
    isPending,
    isRejected,
    isAdmin,
    isEditor,
    isViewer,
    canEdit: isActive && (isAdmin || isEditor || !hasOrganization),
    canManageMembers: isActive && isAdmin,
    canCheckInOut: isActive,
    canViewInventory: isActive,
    hasOrganization,
  };
}
